import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Spinner } from "react-bootstrap";
import axios from "axios";

// maplibre-gl is loaded via CDN in public/index.html

// Country name -> ISO 3166-1 alpha-3 for AWS IncludeCountries filter
const COUNTRY_NAME_TO_ALPHA3 = {
  "united states": "USA",
  "united states of america": "USA",
  usa: "USA",
  us: "USA",
  canada: "CAN",
  "united kingdom": "GBR",
  uk: "GBR",
  "great britain": "GBR",
  australia: "AUS",
  "new zealand": "NZL",
  india: "IND",
  mexico: "MEX",
  germany: "DEU",
  france: "FRA",
  spain: "ESP",
  italy: "ITA",
  netherlands: "NLD",
  belgium: "BEL",
  switzerland: "CHE",
  austria: "AUT",
  sweden: "SWE",
  norway: "NOR",
  denmark: "DNK",
  finland: "FIN",
  ireland: "IRL",
  portugal: "PRT",
  poland: "POL",
  brazil: "BRA",
  argentina: "ARG",
  colombia: "COL",
  chile: "CHL",
  peru: "PER",
  china: "CHN",
  japan: "JPN",
  "south korea": "KOR",
  singapore: "SGP",
  malaysia: "MYS",
  indonesia: "IDN",
  thailand: "THA",
  vietnam: "VNM",
  philippines: "PHL",
  "south africa": "ZAF",
  nigeria: "NGA",
  kenya: "KEN",
  egypt: "EGY",
  "saudi arabia": "SAU",
  uae: "ARE",
  "united arab emirates": "ARE",
  israel: "ISR",
  turkey: "TUR",
  russia: "RUS",
  ukraine: "UKR",
  pakistan: "PAK",
  bangladesh: "BGD",
  "sri lanka": "LKA",
  nepal: "NPL",
};

const getAlpha3 = (name) =>
  name ? COUNTRY_NAME_TO_ALPHA3[name.toLowerCase().trim()] || null : null;

const AWS_REGION = process.env.REACT_APP_AWS_REGION || "us-east-1";
const AWS_API_KEY = process.env.REACT_APP_AWS_API_KEY || "";
const AWS_MAP_STYLE = process.env.REACT_APP_AWS_MAP_STYLE || "Standard";

const MAP_STYLE_URL = `https://maps.geo.${AWS_REGION}.amazonaws.com/v2/styles/${AWS_MAP_STYLE}/Light/descriptor?key=${AWS_API_KEY}`;

const asText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.Name || value.Code || "";
  return String(value);
};

const extractCity = (addr) => {
  // AWS Places address components vary by country and query type (place vs reverse-geocode).
  // Prefer locality/municipality-like fields and avoid using SubRegion (often county) as "city".
  const preferred =
    asText(addr?.Municipality) ||
    asText(addr?.Locality) ||
    asText(addr?.District) ||
    asText(addr?.SubDistrict) ||
    asText(addr?.Neighborhood) ||
    "";

  if (preferred) {
    return preferred;
  }

  const subRegionName = asText(addr?.SubRegion);
  if (subRegionName && !/county/i.test(subRegionName)) {
    return subRegionName;
  }

  return "";
};

const reverseGeocode = async (lng, lat) => {
  const res = await axios.post(
    `https://places.geo.${AWS_REGION}.amazonaws.com/v2/reverse-geocode?key=${AWS_API_KEY}`,
    { QueryPosition: [lng, lat], MaxResults: 1 },
  );
  const item = res.data?.ResultItems?.[0];
  if (!item) return null;

  const addr = item.Address || {};
  const pos = item.Position || [lng, lat];
  const city = extractCity(addr);
  const stateCode = addr.Region?.Code || "";
  const stateName = addr.Region?.Name || "";
  const county = asText(addr.SubRegion) || "";
  const countryName = addr.Country?.Name || "";
  const postalRaw = asText(addr.PostalCode) || "";
  const zip = postalRaw.match(/\d{5}/)?.[0] || postalRaw;
  const label =
    addr.Label || [addr.AddressNumber, addr.Street].filter(Boolean).join(" ");

  return {
    address1: label,
    address2: "",
    street: [addr.AddressNumber, addr.Street].filter(Boolean).join(" "),
    city,
    state: stateCode,
    stateName,
    zip,
    county,
    country: countryName,
    lat: pos[1],
    lng: pos[0],
    displayLine1: label,
    displayLine2: [city, stateCode, zip].filter(Boolean).join(", "),
  };
};

const autocompleteSearch = async (query, countryAlpha3 = null) => {
  const body = { QueryText: query, MaxResults: 6 };
  if (countryAlpha3) body.Filter = { IncludeCountries: [countryAlpha3] };

  const res = await axios.post(
    `https://places.geo.${AWS_REGION}.amazonaws.com/v2/autocomplete?key=${AWS_API_KEY}`,
    body,
  );

  return res.data?.ResultItems || [];
};

const getPlaceDetails = async (placeId) => {
  const res = await axios.get(
    `https://places.geo.${AWS_REGION}.amazonaws.com/v2/place/${encodeURIComponent(
      placeId,
    )}?key=${AWS_API_KEY}`,
  );

  const addr = res.data?.Address || {};
  const pos = res.data?.Position || [];
  const city = extractCity(addr);
  const stateCode = addr.Region?.Code || "";
  const stateName = addr.Region?.Name || "";
  const countryName = addr.Country?.Name || "";
  const postalRaw = asText(addr.PostalCode) || "";
  const zip = postalRaw.match(/\d{5}/)?.[0] || postalRaw;
  const label = addr.Label || res.data?.Title || "";

  return {
    address1: label,
    address2: "",
    street: [addr.AddressNumber, addr.Street].filter(Boolean).join(" "),
    city,
    state: stateCode,
    stateName,
    zip,
    county: asText(addr.SubRegion) || "",
    country: countryName,
    lat: pos[1],
    lng: pos[0],
    displayLine1: label,
    displayLine2: [city, stateCode, zip].filter(Boolean).join(", "),
  };
};

const AddressPickerModal = ({ show, onHide, onConfirm, countryLabel }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const [mapError, setMapError] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [preview, setPreview] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);

  const placeMarker = useCallback((lng, lat, preloadedAddr = null) => {
    // eslint-disable-next-line dot-notation
    const mgl = window["maplibregl"];
    if (!mgl || !mapRef.current) return;

    if (markerRef.current) {
      markerRef.current.setLngLat([lng, lat]);
    } else {
      const el = document.createElement("div");
      el.style.cssText = `
        width:28px;height:34px;cursor:pointer;
        background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 30'%3E%3Cpath d='M12 0C5.37 0 0 5.37 0 12c0 9 12 18 12 18s12-9 12-18C24 5.37 18.63 0 12 0zm0 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8z' fill='%230d6efd'/%3E%3C/svg%3E") no-repeat center/contain;
      `;
      markerRef.current = new mgl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(mapRef.current);
    }

    if (preloadedAddr) {
      setPreview(preloadedAddr);
      return;
    }

    setGeocoding(true);
    setPreview(null);
    reverseGeocode(lng, lat)
      .then((addr) => {
        if (addr) setPreview(addr);
      })
      .catch(() => {
        setPreview({
          address1: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          address2: "",
          displayLine1: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          displayLine2: "Coordinates only",
          lat,
          lng,
          street: "",
          city: "",
          state: "",
          stateName: "",
          zip: "",
          county: "",
          country: "",
        });
      })
      .finally(() => setGeocoding(false));
  }, []);

  useEffect(() => {
    if (!show) return;
    setPreview(null);
    setSearchQuery("");
    setSearchResults([]);
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    if (!mapContainerRef.current) return undefined;

    // eslint-disable-next-line dot-notation
    const mgl = window["maplibregl"];
    if (!mgl) {
      setMapError(true);
      return undefined;
    }

    setMapError(false);
    const map = new mgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: [-98.5795, 39.8283], // USA centroid
      zoom: 3.2,
    });
    mapRef.current = map;

    map.on("click", (e) => {
      placeMarker(e.lngLat.lng, e.lngLat.lat);
    });

    return () => {
      try {
        markerRef.current = null;
        mapRef.current = null;
        map.remove();
      } catch {
        // ignore
      }
    };
  }, [show, placeMarker]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setSearchResults([]);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!val || val.length < 3) return;
    searchDebounceRef.current = setTimeout(() => {
      setSearchLoading(true);
      autocompleteSearch(val, getAlpha3(countryLabel))
        .then((items) => setSearchResults(items))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 400);
  };

  const handleSearchSelect = (item) => {
    if (!mapRef.current || !item?.PlaceId) return;
    setSearchQuery(item.Address?.Label || item.Title || "");
    setSearchResults([]);
    setSearchLoading(true);
    getPlaceDetails(item.PlaceId)
      .then((addr) => {
        if (!addr?.lng || !addr?.lat) return;
        mapRef.current.flyTo({
          center: [addr.lng, addr.lat],
          zoom: 16,
          duration: 800,
        });
        placeMarker(addr.lng, addr.lat, addr);
      })
      .catch(() => {})
      .finally(() => setSearchLoading(false));
  };

  const handleUseAddress = () => {
    if (preview) onConfirm(preview);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Pick Address</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        {mapError ? (
          <div style={{ padding: 18 }}>
            Map library not available. Please refresh and try again.
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            <div style={{ position: "relative" }}>
              <input
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search an address..."
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 10,
                  border: "1px solid #dee2e6",
                  padding: "0 12px",
                  outline: "none",
                }}
              />
              {(searchLoading || searchResults.length > 0) && (
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 48,
                    zIndex: 5,
                    background: "#fff",
                    border: "1px solid #e9ecef",
                    borderRadius: 10,
                    overflow: "hidden",
                    boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
                  }}
                >
                  {searchLoading && (
                    <div style={{ padding: 12, display: "flex", gap: 10 }}>
                      <Spinner animation="border" size="sm" />
                      <div style={{ fontSize: 13, color: "#6c757d" }}>
                        Searching...
                      </div>
                    </div>
                  )}
                  {!searchLoading &&
                    searchResults.map((item) => (
                      <button
                        key={item.PlaceId || item.Title}
                        type="button"
                        onClick={() => handleSearchSelect(item)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          background: "#fff",
                          border: "none",
                          borderTop: "1px solid #f1f3f5",
                          padding: "10px 12px",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {item.Address?.Label || item.Title || "Result"}
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div
              ref={mapContainerRef}
              style={{
                marginTop: 12,
                width: "100%",
                height: 380,
                borderRadius: 12,
                overflow: "hidden",
                border: "1px solid #e9ecef",
              }}
            />

            <div style={{ marginTop: 12 }}>
              {geocoding && (
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <Spinner animation="border" size="sm" />
                  <div style={{ fontSize: 13, color: "#6c757d" }}>
                    Resolving address...
                  </div>
                </div>
              )}
              {preview && (
                <div
                  style={{
                    marginTop: 10,
                    border: "1px solid #e9ecef",
                    borderRadius: 12,
                    padding: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    background: "#f8f9fa",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {preview.displayLine1 || preview.street || preview.address1}
                    </div>
                    {preview.displayLine2 && (
                      <div style={{ fontSize: 12, color: "#6c757d" }}>
                        {preview.displayLine2}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={handleUseAddress}
                    style={{
                      whiteSpace: "nowrap",
                      background: "#0d6efd",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 14px",
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    Use this address
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

AddressPickerModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  countryLabel: PropTypes.string,
};

AddressPickerModal.defaultProps = {
  countryLabel: "",
};

export default AddressPickerModal;
